import { Component, ChangeDetectionStrategy, signal, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, FormArray, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ResponseService } from './response.service';
import { AuthService } from '../../core/auth/services/auth.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { startWith, firstValueFrom } from 'rxjs';

/**
 * WHY: Unified response form handling both wedding and polterabend RSVPs
 * SOLID: Single responsibility - manages form state and submission logic
 */
@Component({
    selector: 'app-unified-response-form',
    templateUrl: './unified-response.form.html',
    imports: [CommonModule, RouterModule, ReactiveFormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnifiedResponseFormComponent {
    private fb = inject(FormBuilder);
    private responseService = inject(ResponseService);
    private authService = inject(AuthService);

    // WHY: Input signal for determining form type (wedding/polterabend)
    formType = input<'wedding' | 'polterabend'>('polterabend');

    // WHY: Custom validator to ensure at least one attendance option is selected
    private attendanceValidator(control: AbstractControl): ValidationErrors | null {
        const attendsWedding = control.get('attendsWedding')?.value;
        const attendsPolterabend = control.get('attendsPolterabend')?.value;
        const attendsNothing = control.get('attendsNothing')?.value;

        if (!attendsWedding && !attendsPolterabend && !attendsNothing) {
            return { noAttendanceSelected: true };
        }

        return null;
    }

    // Signals für reaktive State-Verwaltung
    isSubmitting = signal(false);
    showSuccess = signal(false);
    showError = signal<string | null>(null);

    // WHY: Computed signals for reactive state
    hasFullAccess = computed(() => this.authService.hasFullAccess());
    isWeddingForm = computed(() => this.formType() === 'wedding' || this.hasFullAccess());

    // Signal for form changes to trigger reactivity
    private formChanges = signal(0);

    // WHY: Computed signal to check if anyone attends Polterabend
    hasPolterabendAttendees = computed(() => {
        this.formChanges(); // Trigger reactivity
        return this.personsArray.controls.some(control => 
            control.get('attendsPolterabend')?.value === true
        );
    });

    // Computed summary text
    summaryText = computed(() => {
        // WHY: Depend on formChanges signal for reactivity
        this.formChanges();

        const persons = this.personsArray.controls.map(control => ({
            name: control.get('name')?.value || '',
            attendsWedding: control.get('attendsWedding')?.value || false,
            attendsPolterabend: control.get('attendsPolterabend')?.value || false,
            attendsNothing: control.get('attendsNothing')?.value || false
        })).filter(p => p.name.trim() !== '');

        const weddingAndPolter = persons.filter(p => p.attendsWedding && p.attendsPolterabend);
        const onlyPolter = persons.filter(p => !p.attendsWedding && p.attendsPolterabend && !p.attendsNothing);
        const onlyWedding = persons.filter(p => p.attendsWedding && !p.attendsPolterabend && !p.attendsNothing);
        const attendsNothing = persons.filter(p => p.attendsNothing);

        let summary = 'Hier ist meine/unsere Rückmeldung:';

        if (weddingAndPolter.length > 0) {
            summary += `\n- ${weddingAndPolter.map(p => p.name).join(', ')} komm${weddingAndPolter.length === 1 ? 't' : 'en'} zum Polterabend und zur Hochzeit.`;
        }

        if (onlyPolter.length > 0) {
            summary += `\n- ${onlyPolter.map(p => p.name).join(', ')} komm${onlyPolter.length === 1 ? 't' : 'en'} nur zum Polterabend.`;
        }

        if (onlyWedding.length > 0) {
            summary += `\n- ${onlyWedding.map(p => p.name).join(', ')} komm${onlyWedding.length === 1 ? 't' : 'en'} nur zur Hochzeit.`;
        }

        if (attendsNothing.length > 0) {
            summary += `\n- ${attendsNothing.map(p => p.name).join(', ')} komm${attendsNothing.length === 1 ? 't' : 'en'} gar nicht.`;
        }

        return summary;
    });

    responseForm = this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        wants_updates: [true],
        persons: this.fb.array([this.createPersonGroup()]),
        additional_notes: [''],
        // WHY: Polterabend-specific fields
        arrival_time: ['ontime'],
        brings_items: ['']
    });

    get personsArray() {
        return this.responseForm.get('persons') as FormArray;
    }
    readonly personsValueSig = toSignal(
        this.personsArray.valueChanges.pipe(startWith(this.personsArray.value)),
        { initialValue: this.personsArray.value }
    );

    readonly personCount = computed(() => this.personsValueSig().length);

    private createPersonGroup() {
        const group = this.fb.group({
            name: ['', Validators.required],
            attendsWedding: [false],
            attendsPolterabend: [false],
            attendsNothing: [false],
            allergies_comments: [''],
            // WHY: Polterabend-specific fields
            dietary_requirements: ['']
        });

        // WHY: Add validation to ensure at least one attendance option is selected
        group.addValidators(this.attendanceValidator);

        // WHY: Add logic to handle exclusive "kommt gar nicht" selection
        group.get('attendsNothing')?.valueChanges.subscribe(value => {
            if (value) {
                group.get('attendsWedding')?.setValue(false, { emitEvent: false });
                group.get('attendsPolterabend')?.setValue(false, { emitEvent: false });
            }
        });

        const attendsWedding = group.get('attendsWedding');
        const attendsPolterabend = group.get('attendsPolterabend');

        attendsWedding?.valueChanges.subscribe(value => {
            if (value) {
                group.get('attendsNothing')?.setValue(false, { emitEvent: false });
            }
        });

        attendsPolterabend?.valueChanges.subscribe(value => {
            if (value) {
                group.get('attendsNothing')?.setValue(false, { emitEvent: false });
            }
        });

        return group;
    }

    constructor() {
        // WHY: Subscribe to form changes to trigger signal updates for reactivity
        this.responseForm.valueChanges.subscribe(() => {
            this.formChanges.update(count => count + 1);
        });

        // WHY: Email field activation and validation logic
        this.responseForm.get('wants_updates')?.valueChanges.subscribe((wants: boolean | null) => {
            const emailCtrl = this.responseForm.get('email');
            if (wants === true) {
                emailCtrl?.enable();
                // WHY: Add required validator when email updates are wanted
                emailCtrl?.setValidators([Validators.required, Validators.email]);
            } else {
                emailCtrl?.disable();
                // WHY: Remove validators when email updates are not wanted
                emailCtrl?.setValidators([]);
            }
            emailCtrl?.updateValueAndValidity();
        });
    }

    addPerson(): void {
        this.personsArray.push(this.createPersonGroup());
        // WHY: Trigger signal update for reactivity
        this.formChanges.update(count => count + 1);
    }

    removePerson(index: number): void {
        if (this.personsArray.length > 1) {
            this.personsArray.removeAt(index);
            // WHY: Trigger signal update for reactivity
            this.formChanges.update(count => count + 1);
        }
    }

    async onSubmit(): Promise<void> {
        if (this.responseForm.invalid) {
            // Zeige verständliche Fehlermeldung für Validierungsfehler
            this.showError.set('Das Formular ist nicht korrekt ausgefüllt, bitte überprüfe alle Felder.');
            // Markiere alle Felder als "angetastet", damit Fehler angezeigt werden
            this.responseForm.markAllAsTouched();
            return;
        }

        this.isSubmitting.set(true);
        this.showError.set(null);

        try {
            const formData = this.responseForm.value;

            // Determine endpoint based on access level
            if (this.hasFullAccess()) {
                // Submit to wedding endpoint - map to wedding person format
                const weddingPersons = (formData.persons || []).map(p => ({
                    name: p?.name || '',
                    attendsWedding: p?.attendsWedding || false,
                    attendsPolterabend: p?.attendsPolterabend || false,
                    attendsNothing: p?.attendsNothing || false,
                    allergies_comments: p?.allergies_comments || undefined,
                    brings_items: formData.brings_items || undefined,
                    dietary_requirements: p?.dietary_requirements || undefined
                }));

                await firstValueFrom(this.responseService.submitRSVP({
                    email: formData.email || '',
                    wants_updates: true,
                    persons: weddingPersons,
                    additional_notes: formData.additional_notes || undefined
                }));
            } else {
                // Submit to polterabend endpoint - map to polterabend person format
                const polterabendPersons = (formData.persons || []).map(p => ({
                    name: p?.name || '',
                    attendsWedding: p?.attendsWedding || false,
                    attendsPolterabend: p?.attendsPolterabend || false,
                    attendsNothing: p?.attendsNothing || false,
                    allergies_comments: p?.allergies_comments || undefined,
                    brings_items: formData.brings_items || undefined,
                    dietary_requirements: p?.dietary_requirements || undefined
                }));

                await firstValueFrom(this.responseService.submitPolterabendRSVP({
                    email: formData.email || '',
                    wants_updates: true,
                    persons: polterabendPersons,
                    additional_notes: formData.additional_notes || undefined,
                    arrival_time: 'ontime' as const,
                    event_type: 'polterabend' as const
                }));
            }

            this.showSuccess.set(true);
            // WHY: Don't reset form immediately, let user see success overlay first

        } catch (error) {
            console.error('Submission error:', error);
            this.showError.set('Fehler beim Verarbeiten oder Senden. Bitte überprüft alle Felder, versucht es nochmal oder schreibt uns direkt.');
        } finally {
            this.isSubmitting.set(false);
        }
    }

    // WHY: Polterabend-specific options as signal
    arrivalOptions = signal([
        { value: 'early', label: 'Früh (gegen 15:00 - helfen beim Aufbau)' },
        { value: 'ontime', label: 'Gegen 19:00' },
        { value: 'late', label: 'Später/ nach dem Essen' }
    ]);


    // WHY: Method to close success overlay and reset form
    closeSuccessOverlay(): void {
        this.showSuccess.set(false);
        this.responseForm.reset();
        this.personsArray.clear();
        this.personsArray.push(this.createPersonGroup());
        // WHY: Trigger signal update after form reset
        this.formChanges.update(count => count + 1);
    }
}